import {
    ApiShopperLandingPageLink,
    ApiPromotion,
    VLPVariant,
} from '@storefronts/common-types'
import {
    calculateDiscountPercentage,
    getBestDiscountsForProducts,
    getDiscountValue,
    hasShopifyId,
    promotionAppliesToProduct,
} from './discounts'
import { transformLinkToVLPVariant } from './helpers'

/**
 * Get the currency symbol to use, handling null/empty cases
 * @param currencySymbol Optional currency symbol
 * @returns The currency symbol to use, or null if no symbol should be used
 */
const getCurrencySymbol = (currencySymbol?: string | null): string | null => {
    if (currencySymbol === null || currencySymbol === '') {
        return null
    }
    return currencySymbol ?? '$'
}

export const priceFormat = (
    price: number,
    currencySymbol?: string | null,
): string => {
    const formattedPrice = (price / 100).toFixed(2)
    const symbol = getCurrencySymbol(currencySymbol)
    if (symbol === null) {
        return formattedPrice
    }
    return `${symbol}${formattedPrice}`
}

export const formatPriceWithoutSymbol = (price: number): string => {
    return (price / 100).toFixed(2)
}

/**
 * Format a price without cents (whole dollars/currency units only)
 * Uses Math.ceil to round up, ensuring the displayed value is at or above
 * the actual threshold to prevent user confusion (e.g., $50.99 threshold
 * displays as "$51" not "$50").
 * @param price Price in cents
 * @param currencySymbol Optional currency symbol (defaults to '$')
 * @returns Formatted price string without cents (e.g., "$50" or "Rs.51")
 */
export const formatPriceWithoutCents = (
    price: number,
    currencySymbol?: string | null,
): string => {
    const dollars = Math.ceil(price / 100)
    const symbol = getCurrencySymbol(currencySymbol)
    if (symbol === null) {
        return dollars.toString()
    }
    return `${symbol}${dollars}`
}

export const splitDollarsAndCents = (price: string) => {
    const parts = price.split('.')
    const dollars = parts[0] || '0'
    const cents = parts[1]?.padStart(2, '0') || '00'
    return { dollars, cents }
}

export const convertPriceToCents = (price?: string) => {
    if (!price) {
        return
    }
    const priceNumber = Number(price)
    if (isNaN(priceNumber)) {
        return
    }
    return Math.round(priceNumber * 100)
}

export type Prices = {
    price: number
    compareAtPrice: number | null
    discountPercentage: number | null
}

/** Synced from Shopify or resolved against the synced discounts. */
const isKnownProductScoped = (d: ApiPromotion): boolean =>
    d.productScope != null || hasShopifyId(d)

/**
 * Percent discounts always apply. Fixed amounts require canApplyFixed (display
 * prices with a link — see getCartLinePrice) and a discount known to target
 * this product.
 */
const applyDiscounts = (
    basePrice: number,
    discounts: ApiPromotion[] | null,
    canApplyFixed: boolean,
): number => {
    if (!discounts) return basePrice
    let price = basePrice
    for (const d of discounts) {
        const isPercent = d.discountType.toLowerCase() === 'percent'
        if (isPercent || (canApplyFixed && isKnownProductScoped(d))) {
            price = Math.max(0, price - getDiscountValue(d, price))
        }
    }
    return price
}

/**
 * Computes final price, compare-at price, and discount percentage for a variant.
 * When `link` is provided, product-level discounts from the link are applied (and combined with page discount when applicable).
 */
export const getVariantPrices = (
    variant: VLPVariant,
    discount: ApiPromotion | null,
    link?: ApiShopperLandingPageLink | null,
): Prices => computeVariantPrices(variant, discount, link, link != null)

/**
 * Price to store on a cart line. Applies percent discounts only — the cart
 * pipeline accounts for fixed discounts itself (page-level subtraction and
 * Shopify allocations), so baking them in here would double-count.
 */
export const getCartLinePrice = (
    link: ApiShopperLandingPageLink,
    discount: ApiPromotion | null,
    activeVariant?: VLPVariant | null,
): number =>
    computeVariantPrices(
        activeVariant ?? transformLinkToVLPVariant(link),
        discount,
        link,
        false,
    ).price

const computeVariantPrices = (
    variant: VLPVariant,
    discount: ApiPromotion | null,
    link: ApiShopperLandingPageLink | null | undefined,
    applyFixed: boolean,
): Prices => {
    const basePrice = variant.price.price

    // A page discount scoped away from this product behaves as no page discount
    const pageDiscount =
        discount !== null &&
        promotionAppliesToProduct(discount, link?.shopifyId)
            ? discount
            : null
    let discounts: ApiPromotion[] | null =
        pageDiscount !== null ? [pageDiscount] : null
    if (link?.productDiscounts?.length) {
        discounts = getBestDiscountsForProducts(link, pageDiscount, basePrice)
    }
    let finalPrice = applyDiscounts(basePrice, discounts, applyFixed)

    // Custom discount (admin-configured metafield, display-only — see
    // CustomPriceSetting) never stacks with existing discounts/sales: take
    // whichever price is lower. Scoped to PDP display (applyFixed === link
    // != null) — cart line price (getCartLinePrice) is untouched.
    if (applyFixed && variant.customDiscountedPrice != null) {
        finalPrice = Math.min(finalPrice, variant.customDiscountedPrice)
    }

    let compareAtPrice: number | null = variant.price.compareAtPrice ?? null
    if (
        (!compareAtPrice || compareAtPrice < basePrice) &&
        finalPrice < basePrice
    ) {
        compareAtPrice = basePrice
    }
    const discountPercentage = calculateDiscountPercentage(
        compareAtPrice ?? 0,
        finalPrice,
    )
    if (compareAtPrice != null && compareAtPrice <= finalPrice) {
        compareAtPrice = null
    }
    return {
        price: finalPrice,
        compareAtPrice,
        discountPercentage: discountPercentage > 0 ? discountPercentage : null,
    }
}

/**
 * Computes prices for a landing page link using its default variant.
 * Uses transformLinkToVLPVariant for consistent default/synthetic variant resolution with transformLinkToVariant.
 */
export const getLinkPrices = (
    link: ApiShopperLandingPageLink,
    discount: ApiPromotion | null,
): Prices => {
    const defaultVariant = transformLinkToVLPVariant(link)
    return getVariantPrices(defaultVariant, discount, link)
}

/**
 * Returns prices for a product: uses the active variant when provided, otherwise the link's default variant.
 * Use this when you have optional activeVariant (e.g. variant selector) and want a single call instead of
 * branching on getVariantPrices(activeVariant, ...) vs getLinkPrices(link, ...).
 */
export const getProductPrices = (
    link: ApiShopperLandingPageLink,
    discount: ApiPromotion | null,
    activeVariant?: VLPVariant | null,
): Prices => {
    if (activeVariant) {
        return getVariantPrices(activeVariant, discount, link)
    }
    return getLinkPrices(link, discount)
}
